import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { message, Divider, Typography } from 'antd';
import { Main } from './styled';
import Spinner from '../components/utilities/Spinner';
import QuickActionModal from '../components/quickAction/QuickActionModal';
import MyBids from '../components/quickAction/MyBids';
import { ClassicGridViewPageVariant } from '../components/quickAction/QuickActionPageVariants';
import { DataService } from '../config/data/dataService';
import { mapQuickRequestToApplication } from '../components/quickAction/quickActionData';

const PAGE_SIZE = 10;

const isBidOpen = (app, now) => {
  if (!app?.expiresAt) return true;

  const expiresAt = new Date(app.expiresAt).getTime();
  if (Number.isNaN(expiresAt)) return true;

  return expiresAt > now;
};

const QuickAction = () => {
  const [quickRequests, setQuickRequests] = useState([]);
  const [selectedAppId, setSelectedAppId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadingApp, setLoadingApp] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [vendorQuestions, setVendorQuestions] = useState({});
  const [now, setNow] = useState(Date.now());
  const requestIdRef = useRef(0);

  const fetchQuickRequests = useCallback(async (page = 1, search = searchText, silent = false) => {
    const requestId = ++requestIdRef.current;
    try {
      const response = await DataService.post('/getQuickActionList', {
        page: String(page),
        page_items_count: PAGE_SIZE,
        search: search.trim(),
      });
      if (requestId !== requestIdRef.current) return;
      const responseData = response?.data?.data;
      const quickRequests = responseData?.data || [];
      console.log('Quick request list response:', response?.data);
      setQuickRequests(quickRequests);
      setTotalCount(Number(responseData?.total_count) || 0);
      setCurrentPage(Number(responseData?.current_page) || page);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error('Quick request list error:', error);
      if (silent) return;
      message.error('Unable to fetch quick action requests');
      setQuickRequests([]);
      setTotalCount(0);
    }
  }, [searchText]);

  useEffect(() => {
    const fetchVendorQuestions = async () => {
      try {
        const response = await DataService.get('/getVendorQuestions');
        if (response.data.status !== true) {
          throw new Error(response.data.message || 'Unable to fetch vendor questions');
        }
        const questions = Object.fromEntries(
          response.data.data.map(({ id, question }) => [id, question]),
        );
        console.log('Vendor questions response:', response?.data);

        setVendorQuestions(questions);
      } catch (error) {
        console.error('Vendor questions error:', error);
      }
    };

    fetchVendorQuestions();
  }, []);

  useEffect(() => {
    const searchTimer = setTimeout(() => {
      fetchQuickRequests(1);
    }, 350);

    return () => {
      clearTimeout(searchTimer);
      requestIdRef.current += 1;
    };
  }, [fetchQuickRequests]);

  useEffect(() => {
    let stopped = false;
    let pollTimer;

    const poll = async () => {
      if (document.visibilityState === 'visible' && !loadingApp) {
        await fetchQuickRequests(currentPage, searchText, true);
      }
      if (!stopped) pollTimer = setTimeout(poll, 5000);
    };

    pollTimer = setTimeout(poll, 5000);
    return () => {
      stopped = true;
      clearTimeout(pollTimer);
      requestIdRef.current += 1;
    };
  }, [currentPage, fetchQuickRequests, loadingApp, searchText]);

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedAppId(null);
  };

  const removeApplicationFromList = (appId) => {
    setQuickRequests((prevRequests) => prevRequests.filter((request) => request.reference_no !== appId));
    setTotalCount((prevTotalCount) => Math.max(0, prevTotalCount - 1));
  };

  const updateExchangeStatus = async (appId, status, bidAmount = '', reason = '') => {
    const app = applications.find((application) => application.id === appId);
    const referenceNo = app?.referenceNo || appId;

    setLoadingApp(appId);

    try {
      const payload = {
        reference_no: referenceNo,
        status,
        reason,
        bid_amount: bidAmount || '',
      };

      const response = await DataService.post('/updateExchangeStatus', payload);

      if (response?.data?.status === true) {
        requestIdRef.current += 1;
        removeApplicationFromList(appId);
        closeModal();
        return true;
      }

      message.error(response?.data?.message || 'Unable to update request status');
      return false;
    } catch (error) {
      console.error('Quick request status update error:', error);
      message.error('Unable to update request status');
      return false;
    } finally {
      setLoadingApp(null);
    }
  };

  const handleRejectRequest = async (appId, reason) => {
    const updated = await updateExchangeStatus(appId, 'rejected', '', reason);
    if (updated) {
      message.warning(`Request for ${appId} declined`);
    }
  };

  const handleDirectAccept = async (appId, bidAmount) => {
    const updated = await updateExchangeStatus(appId, 'accepted', bidAmount);
    if (updated) {
      message.success(`Request accepted successfully for ${appId}`);
    }
  };

  const handleCardClick = (appId) => {
    setSelectedAppId(appId);
    setIsModalOpen(true);
  };

  const handlePageChange = (page) => {
    closeModal();
    fetchQuickRequests(page);
  };

  const applications = useMemo(
    () => quickRequests.map((request) => mapQuickRequestToApplication(request, vendorQuestions)),
    [quickRequests, vendorQuestions],
  );
  const activeApplications = applications.filter((app) => app.status === 'Pending' && isBidOpen(app, now));
  const selectedApp = activeApplications.find((app) => app.id === selectedAppId);
  const pendingCount = activeApplications.length;
  const topPendingApp = activeApplications[0];

  useEffect(() => {
    if (selectedAppId && !selectedApp) {
      closeModal();
    }
  }, [selectedAppId, selectedApp]);

  const variantProps = {
    filteredApplications: activeApplications,
    searchText,
    onSearchChange: setSearchText,
    pendingCount,
    topPendingApp,
    pagination: {
      current: currentPage,
      pageSize: PAGE_SIZE,
      total: totalCount,
      onChange: handlePageChange,
    },
    onCardClick: handleCardClick,
    refreshData: () => fetchQuickRequests(currentPage),
    now,
  };

  return (
    <Main>
      <Suspense fallback={<Spinner />}>
        <div>
          <Typography.Title level={3} style={{ marginTop: 0 }}>Quick Actions</Typography.Title>
          <section aria-label="New Requests">
            <ClassicGridViewPageVariant {...variantProps} />
          </section>
          <Divider style={{ margin: '32px 0' }} />
          <section aria-label="My Bids">
            <MyBids now={now} questionLabels={vendorQuestions} />
          </section>
          <QuickActionModal
            app={selectedApp}
            open={isModalOpen}
            loadingApp={loadingApp}
            onClose={closeModal}
            onRejectRequest={handleRejectRequest}
            onDirectAccept={handleDirectAccept}
            now={now}
          />
        </div>
      </Suspense>
    </Main>
  );
};

export default QuickAction;
